/**
 * Inbox — texts and emails with customers (`docs/activity.md` §7, Boards 7,
 * 8 and 16 A–B).
 *
 * The toolbar (*Search conversations…* · **Unread · <n>** · **All** · and
 * **All · Texts · Email** on the right), the list (340), the thread, and the
 * customer beside it (300). Whose conversations is the people button's
 * answer — Unassigned is picked there, never here (§7.9). What it shows is
 * remembered (`state.inbox`); `?c=<id>` opens one, and `?contact=<id>&
 * channel=sms|email` opens a customer's with the composer on that channel.
 * On a phone the list is the screen and a thread opens over it (§7.2).
 *
 * Each person keeps their own list (§7.10, Aaron, Oct 24): **Archived** beside
 * Unread and All, a **Category** filter once they have categories, and a
 * right-click on a row — a long press on a phone — to mark it unread, pin it,
 * archive it or file it (`marks.tsx`).
 */
import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react'
import { useSearchParams } from 'react-router-dom'
import type { ConversationThread, InboxChannel } from '@rowted/shared'
import { usePhone } from '../../lib/use-narrow.js'
import { activityRequest } from '../api.js'
import { useActivity } from '../activity-context.js'
import { openActivityNew } from '../calendar/overlays.js'
import { Glyph } from '../glyphs.js'
import { useActivityNav } from '../use-activity-history.js'
import { setOpenThread } from './actions.js'
import { Composer } from './composer.js'
import { ContactPanel } from './contact-panel.js'
import { ConversationRowView, MoreSentinel, SwipeRow, type RowLook } from './conversation-list.js'
import { FilterChip, Seg } from './look.js'
import { CategoryFilter, useInboxMarks } from './marks.js'
import { AssignSheet, PhoneThread } from './phone-thread.js'
import { Messages, ThreadHead, useUnreadAtOpen } from './thread.js'
import { useConversations, useThread } from './use-inbox.js'

/** *All · Texts · Email* — the switch on a desktop and an iPad, the page bar's view button on a phone. */
export const CHANNELS = [
  { value: 'all', label: 'All' },
  { value: 'sms', label: 'Texts' },
  { value: 'email', label: 'Email' },
] as const

const isChannel = (value: string | null): value is InboxChannel => value === 'sms' || value === 'email'

/** The space under the page head, to the bottom of the window — the list and the thread scroll inside it. */
function useFill(enabled: boolean) {
  const ref = useRef<HTMLDivElement | null>(null)
  const [height, setHeight] = useState<number | null>(null)
  const measure = useCallback(() => {
    const element = ref.current
    if (!enabled || !element) return
    const top = element.getBoundingClientRect().top
    setHeight(Math.max(480, Math.round(window.innerHeight - top)))
  }, [enabled])
  useLayoutEffect(measure)
  useEffect(() => {
    window.addEventListener('resize', measure)
    return () => window.removeEventListener('resize', measure)
  }, [measure])
  return { ref, height }
}

/** The customer beside the thread only where the thread keeps room to read (340 + 520 + 300). */
const SIDE_FROM = 1160

function useWidth(element: HTMLElement | null): number {
  const [width, setWidth] = useState(0)
  useEffect(() => {
    if (!element) return
    const watcher = new ResizeObserver(() => setWidth(element.clientWidth))
    watcher.observe(element)
    setWidth(element.clientWidth)
    return () => watcher.disconnect()
  }, [element])
  return width
}

export function InboxTab() {
  const { me, state, change, directory, people, unassigned, reloadBadge, today } = useActivity()
  const phone = usePhone()
  const [params] = useSearchParams()
  const nav = useActivityNav()
  const [search, setSearch] = useState('')
  const [q, setQ] = useState('')
  const [asked, setAsked] = useState<InboxChannel | null>(null)
  const [swiped, setSwiped] = useState<string | null>(null)
  const [assigning, setAssigning] = useState<ConversationThread | null>(null)
  const [listBox, setListBox] = useState<HTMLDivElement | null>(null)
  const fill = useFill(!phone)
  const [body, setBody] = useState<HTMLDivElement | null>(null)
  const wide = useWidth(body) >= SIDE_FROM

  useEffect(() => {
    const timer = window.setTimeout(() => setQ(search), 250)
    return () => window.clearTimeout(timer)
  }, [search])

  const list = useConversations({
    people, unassigned, filter: state.inbox.filter, channel: state.inbox.channel,
    category: state.inbox.categoryId ?? null, q,
  })

  /*
   * Reading is recorded when somebody opens a conversation — a click, a link,
   * a stop card's *Text* — never when the page opens the newest one by itself
   * (§7.1; Board 7 draws it open and still unread). The badge follows.
   */
  const read = useRef(new Set<string>())
  const markRead = useCallback((id: string) => {
    if (read.current.has(id)) return
    read.current.add(id)
    activityRequest(`/conversations/${id}/read`, { method: 'POST', body: {} })
      .then(() => reloadBadge())
      .catch(() => { read.current.delete(id) })
  }, [reloadBadge])

  /*
   * Which one is open is `?c=` — the address is the one source (Aaron, Oct 26), so back steps
   * through the conversations opened and a reload opens the same one. On a desktop, with none
   * named, the page opens the one remembered when it is in the list, else the newest, and writes
   * it into this entry — not read (§7.1), and not an entry of its own.
   */
  const selected = nav.get('c')
  const linked = selected
  const { push: pushNav, replace: replaceNav, close: closeNav, opened: openedNav, wroteItself, layerUnder } = nav
  /*
   * Read when the address comes to name it — and only then: `markRead` is new whenever the page's
   * state is (a press remembers the conversation), and run again for the one still drawn while the
   * address has moved on to the next, it would read the one the page had opened by itself.
   */
  const readNow = useRef({ markRead, wroteItself })
  readNow.current = { markRead, wroteItself }
  useEffect(() => {
    if (!linked || readNow.current.wroteItself('c')) return
    readNow.current.markRead(linked)
  }, [linked])
  const remembered = state.inbox.conversationId
  const pick = useCallback((id: string) => replaceNav({ c: id }, { auto: true }), [replaceNav])
  useEffect(() => {
    if (phone || selected || list.loading || params.get('contact')) return
    const first = list.rows.find((row) => row.id === remembered) ?? list.rows[0]
    if (first) pick(first.id)
  }, [phone, selected, list.loading, list.rows, params, remembered, pick])
  /* A link that opened a thread on a phone (the bell, a record) gets the list under it: back is the list. */
  const layering = useRef({ openedNav, layerUnder })
  layering.current = { openedNav, layerUnder }
  useEffect(() => {
    if (phone && linked && !layering.current.openedNav('c')) layering.current.layerUnder(['c'])
  }, [phone, linked])

  /**
   * Opens a conversation: a press is going somewhere (`push` — back comes back here); anything
   * else changes this entry (`replace`). Nothing (`null`) closes the phone's thread, which is back.
   */
  const open = useCallback((id: string | null, channel: InboxChannel | null = null, how: 'push' | 'replace' = 'replace') => {
    setAsked(channel)
    if (id) {
      change({ inbox: { conversationId: id } })
      markRead(id)
    }
    const set = { c: id, contact: null, channel: null }
    if (id && how === 'push') pushNav(set)
    else if (!id && phone && selected) closeNav(['c'])
    /* A stop card's *Text* names the customer; the conversation found is what that entry opened. */
    else replaceNav(set, { opens: Boolean(id) })
  }, [change, markRead, phone, selected, pushNav, closeNav, replaceNav])

  /* Somebody else picked in the people button: their newest opens, not the last one of whoever was before. */
  const whose = `${people.join(',')}|${unassigned.join(',')}`
  const lastWhose = useRef(whose)
  useEffect(() => {
    if (lastWhose.current === whose) return
    lastWhose.current = whose
    open(null)
  }, [whose, open])

  /* `?contact=&channel=` from a stop card (§6.5): the customer's conversation, else a New text or email. */
  const wantedContact = params.get('contact')
  const wantedChannel = params.get('channel')
  useEffect(() => {
    if (!wantedContact || list.loading) return
    const channel = isChannel(wantedChannel) ? wantedChannel : null
    const found = list.rows.find((row) => row.contactId === wantedContact)
    if (found) {
      open(found.id, channel)
      return
    }
    /* Not on this page of the list: ask the server whose conversation it is, before starting a new one. */
    let live = true
    activityRequest<{ id: string }>(`/conversations/by-contact/${wantedContact}`)
      .then((answer) => { if (live) open(answer.id, channel) })
      .catch(() => {
        if (!live) return
        open(null)
        openActivityNew({ kind: channel === 'email' ? 'email' : 'text', prefill: { contactId: wantedContact } })
      })
    return () => { live = false }
  }, [wantedContact, wantedChannel, list.loading, list.rows, open])

  const thread = useThread(selected)
  const unreadAtOpen = useUnreadAtOpen(thread.thread)

  /* New ▾ on this tab starts from the conversation that is open (§9.2). */
  useEffect(() => {
    setOpenThread(thread.thread && thread.thread.id === selected ? thread.thread : null)
    return () => setOpenThread(null)
  }, [thread.thread, selected])

  /* A remembered conversation that is no longer this person's to see gives way to the newest. */
  useEffect(() => {
    if (!thread.error || phone || (linked && !wroteItself('c'))) return
    const first = list.rows.find((row) => row.id !== selected)
    if (first) pick(first.id)
  }, [thread.error, phone, linked, list.rows, selected, pick, wroteItself])

  /* Leaving a conversation that was read shows it read, where reading counts (its owner, or anyone for Unassigned). */
  const opened = useRef<{ id: string; clears: boolean } | null>(null)
  useEffect(() => {
    /* A follower's reading is theirs too (Aaron, Oct 26). */
    if (thread.thread) {
      opened.current = { id: thread.thread.id, clears: thread.thread.mine || thread.thread.owner === null || thread.thread.following }
    }
  }, [thread.thread])
  const { patch, setCounts, counts, rows } = list
  const leaving = useRef({ patch, setCounts, counts, rows })
  leaving.current = { patch, setCounts, counts, rows }
  useEffect(() => {
    const before = opened.current
    if (!before || before.id === selected) return
    opened.current = null
    const now = leaving.current
    const row = now.rows.find((one) => one.id === before.id)
    if (before.clears && read.current.has(before.id) && row?.unread) {
      now.patch(before.id, { unread: false })
      if (now.counts) now.setCounts({ ...now.counts, unread: Math.max(0, now.counts.unread - 1) })
    }
    /* Opened again later, with something new in it, it is read again. */
    read.current.delete(before.id)
  }, [selected])

  /* A minute going by brings new messages in. */
  const { reload: reloadList } = list
  const { reload: reloadThread } = thread
  useEffect(() => {
    const every = window.setInterval(() => { reloadList(); reloadThread() }, 60_000)
    return () => window.clearInterval(every)
  }, [reloadList, reloadThread])

  const changed = useCallback(() => { reloadThread(); reloadList(); reloadBadge() }, [reloadThread, reloadList, reloadBadge])

  /* A category filter whose category was removed (here, or on another device) lets go rather than showing nothing. */
  const pickedCategory = state.inbox.categoryId ?? null
  const { categories: myCategories, loading: listLoading } = list
  useEffect(() => {
    if (!pickedCategory || listLoading) return
    if (!myCategories.some((category) => category.id === pickedCategory)) change({ inbox: { categoryId: null } })
  }, [pickedCategory, listLoading, myCategories, change])

  /* The row's menu (§7.10). Marked unread, the open one stays unread on leaving it; archived, the next one opens. */
  const marks = useInboxMarks({
    list, reloadBadge, selected, archivedView: state.inbox.filter === 'archived',
    onLeft: () => open(null),
    onMarkedUnread: (id) => { read.current.delete(id) },
  })
  const replaced = useCallback((id: string) => { open(id); reloadList(); reloadBadge() }, [open, reloadList, reloadBadge])

  const faces = useMemo(() => new Map((directory?.people ?? []).map((person) => [person.id, person])), [directory])
  const look: RowLook = {
    today,
    timeZone: list.timeZone ?? thread.thread?.timeZone ?? 'America/New_York',
    several: people.length + unassigned.length > 1,
    faceOf: (id) => faces.get(id) ?? null,
    phone,
  }

  const unreadCount = list.counts?.unread ?? 0
  const chips = (height: 30 | 32) => (
    <>
      <FilterChip height={height} on={state.inbox.filter === 'unread'} onClick={() => change({ inbox: { filter: 'unread' } })}>
        Unread · {unreadCount}
      </FilterChip>
      <FilterChip height={height} on={state.inbox.filter === 'all'} onClick={() => change({ inbox: { filter: 'all' } })}>
        All
      </FilterChip>
      <FilterChip height={height} on={state.inbox.filter === 'archived'} onClick={() => change({ inbox: { filter: 'archived' } })}>
        Archived
      </FilterChip>
      <CategoryFilter height={height} categories={list.categories} value={state.inbox.categoryId ?? null}
        onChange={(categoryId) => change({ inbox: { categoryId } })} onEdit={marks.editCategories} />
    </>
  )
  const channels = (height: 24 | 26) => (
    <Seg label="Texts or email" height={height} options={CHANNELS} value={state.inbox.channel}
      onChange={(channel) => change({ inbox: { channel } })} />
  )

  const empty = list.loading
    ? 'Loading…'
    : list.error ?? (q.trim() ? `Nothing matches “${q.trim()}”.`
      : state.inbox.filter === 'unread' ? 'Nothing unread.'
        : state.inbox.filter === 'archived' ? 'Nothing archived. Right-click a conversation to archive it.'
          : state.inbox.categoryId ? 'Nothing in this category.' : 'No texts or emails yet.')

  /* Done (§7.2): read for you — on your own conversations and Unassigned ones. */
  const done = async (id: string) => {
    setSwiped(null)
    try {
      await activityRequest(`/conversations/${id}/read`, { method: 'POST', body: {} })
      const row = list.rows.find((one) => one.id === id)
      if (row?.unread) {
        list.patch(id, { unread: false })
        if (list.counts) list.setCounts({ ...list.counts, unread: Math.max(0, list.counts.unread - 1) })
      }
      reloadBadge()
    } catch {
      /* The row stays as it was; the next refresh says what is true. */
    }
  }
  const assign = async (id: string) => {
    setSwiped(null)
    try {
      setAssigning(await activityRequest<ConversationThread>(`/conversations/${id}`))
    } catch {
      /* Nothing to offer. */
    }
  }

  if (phone) {
    return (
      <div className="flex min-h-0 flex-1 flex-col bg-surface">
        {/*
          * Board 16 A's two chips first; Archived and Category (§7.10) scroll in after them.
          * *All · Texts · Email* is the page bar's view button on a phone (Aaron, Oct 25, phone note 8).
          */}
        <div className="flex items-center gap-1.5 border-b border-line-faint bg-surface px-3.5 py-2.5">
          <div className="no-scrollbar -my-1 flex min-w-0 flex-1 items-center gap-1.5 overflow-x-auto py-1">
            {chips(32)}
          </div>
        </div>
        <div>
          {list.rows.length === 0 && <div className="px-4 py-10 text-center text-[13px] text-text-muted">{empty}</div>}
          {list.rows.map((row) => {
            const own = row.ownerId === me.user.id || row.ownerId === null
            return (
              <SwipeRow key={row.id} row={row} look={look} onOpen={() => open(row.id, null, 'push')}
                onDone={own ? () => void done(row.id) : null}
                onAssign={own ? () => void assign(row.id) : null}
                swiped={swiped === row.id} onSwiped={(on) => setSwiped(on ? row.id : null)}
                onLongPress={() => marks.openSheet(row)} />
            )
          })}
          {list.hasMore && <MoreSentinel onMore={list.loadMore} busy={list.loadingMore} root={null} />}
        </div>
        {selected && thread.thread && thread.thread.id === selected && (
          <PhoneThread thread={thread.thread} today={today} asked={asked} onBack={() => open(null)}
            onLeft={() => { open(null); reloadList(); reloadBadge() }}
            onChanged={changed} onReplaced={replaced}
            onRowActions={() => {
              const row = list.rows.find((one) => one.id === thread.thread!.id) ?? thread.thread!.row
              marks.openSheet(row)
            }} />
        )}
        {assigning && <AssignSheet thread={assigning} onClose={() => setAssigning(null)} onDone={changed} />}
        {marks.element}
      </div>
    )
  }

  const shown = thread.thread && thread.thread.id === selected ? thread.thread : null
  return (
    <div className="flex min-h-0 flex-1 flex-col">
      <div className="border-b border-line bg-surface px-5 py-3">
        <div className="flex items-center gap-2">
          <label className="flex h-[34px] w-[230px] shrink-0 items-center gap-2 rounded-[10px] border border-line bg-surface px-[11px] text-[13px] text-text-faint focus-within:border-accent">
            <Glyph name="search" size={15} />
            <input value={search} onChange={(event) => setSearch(event.target.value)} placeholder="Search conversations…"
              aria-label="Search conversations" className="min-w-0 flex-1 bg-transparent text-text outline-none placeholder:text-text-faint" />
          </label>
          {chips(30)}
          <span className="flex-1" />
          {channels(24)}
        </div>
      </div>
      <div ref={(element) => { fill.ref.current = element; setBody(element) }} className="flex min-h-0"
        style={{ height: fill.height ?? undefined }}>
        <div ref={setListBox} className="w-[340px] shrink-0 self-stretch overflow-y-auto border-r border-line bg-surface">
          {list.rows.length === 0 && <div className="px-4 py-10 text-center text-[12.5px] text-text-muted">{empty}</div>}
          {list.rows.map((row) => (
            <ConversationRowView key={row.id} row={row} open={row.id === selected} look={look} onOpen={() => open(row.id, null, 'push')}
              onMenu={marks.openMenu} />
          ))}
          {list.hasMore && <MoreSentinel onMore={list.loadMore} busy={list.loadingMore} root={listBox} />}
        </div>
        <section className="flex min-w-0 flex-1 flex-col self-stretch bg-surface-nav">
          {shown ? (
            <>
              <ThreadHead thread={shown} onChanged={changed} onReplaced={replaced}
                onLeft={() => {
                  /* Not theirs to see any more (Aaron, Oct 26): the next row opens, never the one that is gone. */
                  const next = list.rows.find((row) => row.id !== shown.id)
                  if (next) pick(next.id)
                  else open(null)
                  reloadList()
                  reloadBadge()
                }} />
              <Messages thread={shown} today={today} unreadAtOpen={unreadAtOpen} className="px-[22px] pb-3.5 pt-1" />
              <div className="shrink-0 px-[18px] pb-4 pt-3">
                <Composer key={shown.id} thread={shown} asked={asked} onSent={changed} autoFocus />
              </div>
            </>
          ) : (
            <div className="flex flex-1 items-center justify-center text-[13px] text-text-muted">
              {thread.loading ? 'Loading…' : thread.error ?? (list.rows.length > 0 ? 'Pick a conversation to read it.' : '')}
            </div>
          )}
        </section>
        {shown?.contact && wide && (
          <ContactPanel thread={shown} today={today} onChannel={(channel) => setAsked(channel)} />
        )}
      </div>
      {marks.element}
    </div>
  )
}
